import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — los informes descargables, `/api/reports`. Módulo 14, opción 1.
 *
 * Las cuatro llamadas del vanilla, sin cambios en el contrato. Dos son
 * consultas normales y dos son descargas: `/export` y `/custom/export`
 * devuelven un `.xlsx`, así que no pasan por aquí sino por
 * `Utils.downloadAuthed`, que las pide con el token en la cabecera.
 *
 * **Los filtros de una descarga van en la URL a propósito.** Es la excepción
 * que `docs/Arquitectura/Convenciones.md` deja abierta: un `<a download>` no
 * puede llevar cuerpo, y aquí no viaja ningún dato de nadie — solo el id del
 * área, el del tipo de solicitud y el mes.
 */

export interface InformeDelCatalogo {
  key: string;
  name: string;
  desc: string;
  icon: string;
  /** Qué filtros pide: `mes`, `estado`, `area`, `survey`, `solicitud_tipo`. */
  filters: string[];
}

export interface Catalogo {
  reports: InformeDelCatalogo[];
  areas: Array<{ id: number; name: string }>;
  surveys: Array<{ id: number; title: string; is_anonymous: boolean }>;
  solicitud_tipos: Array<{ id: number; name: string }>;
  meses: Array<{ id: number; name: string }>;
}

export interface DatosDelInforme {
  name: string;
  columns: string[];
  total: number;
  rows: Array<Array<string | number | null>>;
}

export interface Filtros {
  estado: string;
  area_id: string;
  survey_id: string;
  solicitud_type_id: string;
  mes: string;
}

export const FILTROS_VACIOS: Filtros = {
  estado: '', area_id: '', survey_id: '', solicitud_type_id: '', mes: '',
};

export const claves = {
  todo: ['informes'] as const,
  catalogo: ['informes', 'catalogo'] as const,
  datos: (key: string, f: Filtros, limite: number) =>
    ['informes', 'datos', key, f, limite] as const,
};

/** Solo lo que está puesto. Un `area_id` vacío llegaría como texto y el
 *  servidor espera un entero. */
export function parametros(f: Filtros): Record<string, string | number> {
  const p: Record<string, string | number> = {};
  if (f.estado) p.estado = f.estado;
  if (f.area_id) p.area_id = Number(f.area_id);
  if (f.survey_id) p.survey_id = Number(f.survey_id);
  if (f.solicitud_type_id) p.solicitud_type_id = Number(f.solicitud_type_id);
  if (f.mes) p.mes = Number(f.mes);
  return p;
}

/** La misma cuenta, como texto, para las dos rutas de descarga. */
export function consulta(f: Filtros, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(parametros(f))) p.set(k, String(v));
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  return p.toString();
}

/** Qué filtros **exige** cada informe para poder pedirse. El de encuesta
 *  responde 400 sin `survey_id`, y el de solicitudes devuelve una fila de aviso
 *  en vez de datos: en los dos casos la pantalla lo dice antes de pedir. */
export function faltaElegir(informe: InformeDelCatalogo | undefined, f: Filtros): string | null {
  if (!informe) return null;
  if (informe.filters.includes('survey') && !f.survey_id) return 'una encuesta';
  if (informe.filters.includes('solicitud_tipo') && !f.solicitud_type_id) return 'un tipo de solicitud';
  return null;
}

export function useCatalogo() {
  return useQuery({
    queryKey: claves.catalogo,
    queryFn: () => api.get<Catalogo>('/reports/catalog'),
    // El catálogo son diez informes y cuatro listas de filtros: no cambia
    // mientras alguien mira un informe.
    staleTime: 5 * 60_000,
  });
}

/** Los datos de un informe. `limite` es 100 en la vista previa y 1 cuando solo
 *  se quieren los nombres de las columnas, que es como el informe personalizado
 *  sabe qué ofrecer. */
export function useDatos(key: string, filtros: Filtros, habilitado: boolean, limite = 100) {
  return useQuery({
    queryKey: claves.datos(key, filtros, limite),
    queryFn: () => api.get<DatosDelInforme>(`/reports/${key}`, { ...parametros(filtros), limit: limite }),
    enabled: habilitado,
  });
}

/** La descarga. El nombre del archivo lo pone el servidor —lo arma con el
 *  nombre real del informe y la fecha—; el vanilla lo sobreescribía con uno
 *  calculado con `toISOString()`, que es UTC: en Bogotá, desde las 19:00, el
 *  archivo se llamaba como el día siguiente. */
export function descargar(ruta: string) {
  return Utils.downloadAuthed(ruta, '');
}
